import { HStack, IconButton } from "native-base";
import React, { useEffect, useState } from "react";
import { StyleSheet, TouchableHighlight, View } from "react-native";
import {
  faBuilding,
  faCheckCircle,
  faNetworkWired,
  faQrcode,
  faServer,
  faTrashAlt,
} from "../../../assets/consts/faCharCodes";
import Theme from "../../argon-ui/Theme";
import { translate } from "../../locales/translate";
import FontAwesomeIcon from "../FontAwesomeIcon";
import Text from "../TextFont";
import { ServerItemProps } from "./ServerList";
import * as Network from "expo-network";
import checkIPAddress from "../../hooks/checkIPAddress";
import { ArButton } from "../../argon-ui";
import { DEFAULT_SERVER_ID } from "../../../assets/consts";
import QRCodeItem from "../QRCodeItem";
import ModalBase from "../../nativeBase-ui/ModalBase";
import { getDir } from "../../util";
interface Props {
  content: ServerItemProps;
  isSelected: boolean;
  onDeleteServerItem: (serverId: string) => void;
  onSelectedServerItem: (serverId: string) => void;
}

const ServerItem = ({
  content,
  onDeleteServerItem,
  onSelectedServerItem,
  isSelected,
}: Props) => {
  const { serverId, isActive, address, localAddress, buildingName } = content;
  const [networkWifi, setNetworkWifi] = useState(false);
  const [isIPAddress, setIsIPAddress] = useState(false);
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  useEffect(() => {
    const checkNetworkState = async () => {
      let networkType = await Network.getNetworkStateAsync();
      setNetworkWifi(networkType.type == "WIFI");
      address && setIsIPAddress(checkIPAddress(address));
    };
    checkNetworkState();
  }, []);
  const RenderDeleteBox = () => {
    return (
      <View
        style={[
          styles.wrapper,
          {
            backgroundColor: Theme.COLORS.ERROR + 15,
            paddingBottom: 0,
          },
        ]}
      >
        <View style={{ flex: 1 }}>
          {/* === delete message === */}
          <Text style={{ fontSize: 12, marginBottom: 10 }}>
            {translate("serverList.deleteServerItem", {
              address: address || localAddress || "",
            })}
          </Text>
          {/* === delete btn submit === */}
          <HStack space={2}>
            <ArButton
              color="muted"
              style={{ flex: 1 }}
              onPress={() => {
                setShowDeleteMessage(false);
              }}
              variant="outline"
            >
              {translate("app.cancel")}
            </ArButton>
            <ArButton
              color="error"
              onPress={() => {
                onDeleteServerItem(serverId);
                setShowDeleteMessage(false);
              }}
              style={{ flex: 1 }}
            >
              {translate("app.delete")}
            </ArButton>
          </HStack>
        </View>
      </View>
    );
  };
  if (showDeleteMessage) {
    return <RenderDeleteBox />;
  }
  return (
    <>
      <HStack
        space="3"
        style={[
          styles.wrapper,
          { flex: 1 },
          // === Selected Server Style ===
          isSelected && {
            borderWidth: 1,
            borderColor: Theme.COLORS.ACTIVE,
            shadowColor: Theme.COLORS.ACTIVE + "15",
            backgroundColor: Theme.COLORS.ACTIVE + 15,
          },
          // === disable and active server style ===
          !networkWifi && isIPAddress
            ? {
                borderWidth: 1,
                borderColor: Theme.COLORS.STATUS_BAR,
                backgroundColor: Theme.COLORS.LIGHT_MAIN,
              }
            : isActive && styles.isActiveServer,
        ]}
      >
        {/* === QR Code === */}
        {(address || localAddress) && (
          <IconButton
            alignSelf="center"
            colorScheme="error"
            variant="solid"
            style={styles.btnQRCode}
            onPress={() => setShowQRCodeModal(!showQRCodeModal)}
            icon={
              <FontAwesomeIcon
                icon={faQrcode}
                size={16}
                color={Theme.COLORS.PRIMARY}
              />
            }
          />
        )}

        <TouchableHighlight
          onPress={() => onSelectedServerItem(serverId)}
          underlayColor="transparent"
          disabled={!networkWifi && isIPAddress}
          style={{ flex: 1 }}
        >
          <View style={{ flex: 1 }}>
            <HStack justifyContent="space-between" w="full">
              <View>
                {address && (
                  <HStack mb="1.5" alignItems="center">
                    <FontAwesomeIcon
                      icon={faNetworkWired}
                      size={10}
                      color={Theme.COLORS.GRAY_DARK}
                    />
                    <Text
                      style={[
                        { color: Theme.COLORS.GRAY_DARK, marginHorizontal: 3 },
                        styles.textSmall,
                      ]}
                    >
                      {translate("serverList.serverAddress") + ":"}
                    </Text>
                    <Text style={[styles.textSmall]}>
                      {address?.endsWith("/") ? address.slice(0, -1) : address}
                    </Text>
                  </HStack>
                )}
                {localAddress && (
                  <HStack mb="1.5" alignItems="center">
                    <FontAwesomeIcon
                      icon={faNetworkWired}
                      size={10}
                      color={Theme.COLORS.GRAY_DARK}
                    />
                    <Text
                      style={[
                        { color: Theme.COLORS.GRAY_DARK, marginHorizontal: 3 },
                        styles.textSmall,
                      ]}
                    >
                      {translate("serverList.localAddress") + ":"}
                    </Text>
                    <Text style={[styles.textSmall]}>
                      {localAddress?.endsWith("/")
                        ? localAddress.slice(0, -1)
                        : localAddress}
                    </Text>
                  </HStack>
                )}
                {/* === Building Name === */}
                {buildingName && (
                  <HStack mb="1.5" alignItems="center">
                    <FontAwesomeIcon
                      icon={faBuilding}
                      size={10}
                      color={Theme.COLORS.GRAY_DARK}
                    />
                    <Text
                      style={[
                        { color: Theme.COLORS.GRAY_DARK, marginHorizontal: 3 },
                        styles.textSmall,
                      ]}
                    >
                      {translate("app.buildingName") + ":"}
                    </Text>
                    <Text style={[styles.textSmall]}>{buildingName}</Text>
                  </HStack>
                )}
                <HStack space="1.5" alignItems="center">
                  {serverId === DEFAULT_SERVER_ID && (
                    <HStack
                      space="1"
                      alignItems="center"
                      justifyContent="space-around"
                      style={{
                        backgroundColor: Theme.COLORS.SUCCESS + 15,
                        padding: 5,
                        borderRadius: 5,
                        width: 100,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faServer}
                        size={10}
                        color={Theme.COLORS.GRAY_DARK}
                      />
                      <Text style={[styles.textSmall]}>
                        {translate("serverList.defaultServer")}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              </View>
              {/* === Delete btn and active Server === */}
              {isActive ? (
                <View
                  style={[
                    styles.btnRemove,
                    {
                      backgroundColor: Theme.COLORS.SUCCESS + "15",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  ]}
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    size={18}
                    color={Theme.COLORS.SUCCESS}
                  />
                </View>
              ) : (
                serverId !== DEFAULT_SERVER_ID && (
                  <IconButton
                    alignSelf="center"
                    colorScheme="error"
                    variant="solid"
                    style={styles.btnRemove}
                    onPress={() => setShowDeleteMessage(!showDeleteMessage)}
                    icon={
                      <FontAwesomeIcon
                        icon={faTrashAlt}
                        size={16}
                        color={Theme.COLORS.ERROR}
                      />
                    }
                  />
                )
              )}
            </HStack>
          </View>
        </TouchableHighlight>
      </HStack>
      <ModalBase
        style={{
          alignSelf: "center",
          direction: getDir(),
        }}
        header={translate("serverList.scanQRCode")}
        isOpen={showQRCodeModal}
        onClose={() => setShowQRCodeModal(false)}
      >
        <View style={{ padding: 10, marginTop: 5, direction: getDir() }}>
          <Text
            style={{
              fontSize: 12,
              color: Theme.COLORS.MUTED,
              marginBottom: 10,
            }}
          >
            {translate("serverList.QRCodeDesc")}
          </Text>
          {/* === QR Code === */}
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              marginBottom: 15,
            }}
          >
            {(address || localAddress) && (
              <QRCodeItem
                value={address || localAddress}
                // name={buildingInfo?.buildingName}
                size={200}
                containerStyle={[
                  styles.QRCodeContainer,
                  {
                    shadowColor: Theme.COLORS.MUTED,
                    shadowOffset: {
                      width: 0,
                      height: 3,
                    },
                    shadowOpacity: 0.4,
                    shadowRadius: 10,
                    elevation: 4,
                  },
                ]}
              />
            )}
          </View>
          <Text
            style={{
              fontSize: 13,
              color: Theme.COLORS.MUTED,
              marginBottom: 10,
              fontWeight: "bold",
            }}
          >
            {translate("serverList.QRCodeSubDesc")}
          </Text>
        </View>
      </ModalBase>
    </>
  );
};

export default ServerItem;

const styles = StyleSheet.create({
  textSmall: { fontSize: 10 },
  wrapper: {
    borderWidth: 1,
    borderColor: "transparent",
    // flex: 1,
    padding: 10,
    minHeight: 60,
    flexDirection: "row",

    marginBottom: 5,
    borderRadius: 5,

    backgroundColor: Theme.COLORS.WHITE,
    shadowColor: Theme.COLORS.INPUT,
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.3,
    shadowRadius: 6.65,
    elevation: 9,
  },
  isActiveServer: {
    borderColor: Theme.COLORS.PRIMARY,
    borderWidth: 1,
    backgroundColor: Theme.COLORS.PRIMARY + "15",
    shadowColor: Theme.COLORS.PRIMARY + "15",
  },
  btnRemove: {
    backgroundColor: "#fee6eb",
    borderRadius: 10,
    padding: 1,
    width: 40,
    height: 40,
    shadowColor: Theme.COLORS.INPUT,
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.3,
    shadowRadius: 6.65,
    elevation: 9,
  },
  btnQRCode: {
    backgroundColor: Theme.COLORS.WHITE,
    borderColor: Theme.COLORS.PRIMARY,
    borderWidth: 1,
    borderRadius: 10,
    padding: 1,
    width: 40,
    height: 40,
    shadowColor: Theme.COLORS.INPUT,
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.3,
    shadowRadius: 6.65,
    elevation: 9,
  },
  QRCodeContainer: {
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    backgroundColor: Theme.COLORS.LIGHT_MAIN,
    borderColor: Theme.COLORS.BLOCK,
    borderWidth: 1,
    padding: 10,
    borderRadius: 10,
  },
});
